/**
 * TASK-COMBAT-VIZ-007: Unit Animation State Machine Implementation
 *
 * Manages animation state transitions for combat units.
 * Handles idle, walk, attack, cast, and death states with proper priority.
 */

import type { CombatCreature } from '@drawn-of-war/shared/types/combat';

/**
 * Animation states
 */
export enum AnimationState {
  IDLE = 'idle',
  WALK = 'walk',
  ATTACK = 'attack',
  CAST = 'cast',
  DEATH = 'death'
}

/**
 * State update data
 */
export interface StateUpdateData {
  isMoving?: boolean;
  isAttacking?: boolean;
  isCasting?: boolean;
  abilityId?: string;
  health?: number;
  direction?: number;
}

/**
 * State transition event
 */
export interface StateTransition {
  unitId: string;
  previousState: AnimationState;
  newState: AnimationState;
  direction?: number;
}

/**
 * State change callback
 */
type StateChangeCallback = (transition: StateTransition) => void;

/**
 * Animation impact event - fired when attack animation reaches impact frame
 */
export interface AnimationImpactEvent {
  unitId: string;
  estimatedImpactTick: number;
  attackType: 'melee' | 'ranged' | 'ability';
}

/**
 * Animation impact callback
 */
type AnimationImpactCallback = (event: AnimationImpactEvent) => void;

/**
 * Scheduled impact info
 */
interface ScheduledImpact {
  unitId: string;
  estimatedImpactTick: number;
  attackType: 'melee' | 'ranged' | 'ability';
  scheduledTime: number;
  fired: boolean;
}

/**
 * Unit state entry
 */
interface UnitStateEntry {
  currentState: AnimationState;
  isDead: boolean;
}

/**
 * UnitAnimationStateMachine
 *
 * Manages animation state transitions for combat units with priority-based state resolution.
 */
export class UnitAnimationStateMachine {
  private unitStates: Map<string, UnitStateEntry> = new Map();
  private stateChangeCallbacks: StateChangeCallback[] = [];
  private animationImpactCallbacks: AnimationImpactCallback[] = [];
  private scheduledImpacts: ScheduledImpact[] = [];

  /**
   * Register a new unit with initial state
   */
  public registerUnit(unit: CombatCreature): void {
    this.unitStates.set(unit.unitId, {
      currentState: AnimationState.IDLE,
      isDead: unit.status === 'dead'
    });
  }

  /**
   * Unregister a unit
   */
  public unregisterUnit(unitId: string): void {
    this.unitStates.delete(unitId);
  }

  /**
   * Get all registered unit IDs
   * Used for bulk operations like resetting all units to idle on disconnect
   */
  public getAllUnitIds(): string[] {
    return Array.from(this.unitStates.keys());
  }

  /**
   * TASK-DEATH-004: Transition unit to death state
   * Called when death sequence starts
   */
  public transitionToDeath(unitId: string): void {
    const entry = this.unitStates.get(unitId);
    if (!entry) {
      return; // Unit not registered
    }

    const previousState = entry.currentState;
    entry.isDead = true;
    entry.currentState = AnimationState.DEATH;

    if (previousState !== AnimationState.DEATH) {
      this.triggerStateChange({
        unitId,
        previousState,
        newState: AnimationState.DEATH
      });
    }
  }

  /**
   * Update unit state based on combat data
   */
  public updateState(unitId: string, data: StateUpdateData): void {
    const entry = this.unitStates.get(unitId);
    if (!entry) {
      return; // Unit not registered
    }

    const previousState = entry.currentState;

    // Determine new state based on priority
    const newState = this.resolveState(entry, data);

    // Update death flag if health is 0
    if (data.health !== undefined && data.health <= 0) {
      entry.isDead = true;
    }

    // Trigger transition if state changed OR direction is provided (direction update on same state)
    if (newState !== previousState || data.direction !== undefined) {
      entry.currentState = newState;
      this.triggerStateChange({
        unitId,
        previousState,
        newState,
        direction: data.direction
      });
    }
  }

  /**
   * Handle animation completion
   */
  public onAnimationComplete(unitId: string, completedState: AnimationState): void {
    const entry = this.unitStates.get(unitId);
    if (!entry) {
      return;
    }

    // Only transition if still in the completed state
    if (entry.currentState !== completedState) {
      return;
    }

    // Determine next state after animation completes
    let nextState: AnimationState;

    switch (completedState) {
      case AnimationState.ATTACK:
      case AnimationState.CAST:
        // One-shot animations return to idle
        nextState = AnimationState.IDLE;
        break;

      case AnimationState.DEATH:
      case AnimationState.WALK:
      case AnimationState.IDLE:
        // Looping or terminal states stay in current state
        nextState = entry.currentState;
        break;

      default:
        nextState = AnimationState.IDLE;
    }

    if (nextState !== entry.currentState) {
      const previousState = entry.currentState;
      entry.currentState = nextState;
      this.triggerStateChange({
        unitId,
        previousState,
        newState: nextState
      });
    }
  }

  /**
   * Get current state for a unit
   */
  public getState(unitId: string): AnimationState | null {
    const entry = this.unitStates.get(unitId);
    return entry ? entry.currentState : null;
  }

  /**
   * Check if unit is in specific state
   */
  public isInState(unitId: string, state: AnimationState): boolean {
    return this.getState(unitId) === state;
  }

  /**
   * Get all units in specific state
   */
  public getUnitsInState(state: AnimationState): string[] {
    const units: string[] = [];
    for (const [unitId, entry] of this.unitStates.entries()) {
      if (entry.currentState === state) {
        units.push(unitId);
      }
    }
    return units;
  }

  /**
   * Register callback for state changes
   */
  public onStateChange(callback: StateChangeCallback): void {
    this.stateChangeCallbacks.push(callback);
  }

  /**
   * Resolve animation state based on priority
   * Priority: DEATH > CAST > ATTACK > WALK > IDLE
   */
  private resolveState(entry: UnitStateEntry, data: StateUpdateData): AnimationState {
    // Death has highest priority (terminal state)
    if (entry.isDead || (data.health !== undefined && data.health <= 0)) {
      return AnimationState.DEATH;
    }

    // Cast has second priority
    if (data.isCasting) {
      return AnimationState.CAST;
    }

    // Attack has third priority
    if (data.isAttacking) {
      return AnimationState.ATTACK;
    }

    // Walk has fourth priority
    if (data.isMoving) {
      return AnimationState.WALK;
    }

    // Default to idle
    return AnimationState.IDLE;
  }

  /**
   * Trigger state change callbacks
   */
  private triggerStateChange(transition: StateTransition): void {
    this.stateChangeCallbacks.forEach(callback => callback(transition));
  }

  /**
   * Register callback for animation impact events
   * Phase 2: Used by ActionCorrelationService to synchronize damage display
   */
  public onAnimationImpact(callback: AnimationImpactCallback): void {
    this.animationImpactCallbacks.push(callback);
  }

  /**
   * Schedule an animation impact callback
   * Called when an attack starts to schedule impact at the right time
   * @param unitId The attacking unit
   * @param estimatedImpactTick Server tick when damage should apply
   * @param attackType Type of attack
   * @param impactOffsetMs Milliseconds until impact frame
   */
  public scheduleImpact(
    unitId: string,
    estimatedImpactTick: number,
    attackType: 'melee' | 'ranged' | 'ability',
    impactOffsetMs: number
  ): void {
    const scheduledTime = performance.now() + impactOffsetMs;

    this.scheduledImpacts.push({
      unitId,
      estimatedImpactTick,
      attackType,
      scheduledTime,
      fired: false
    });
  }

  /**
   * Update scheduled impacts - call this every frame
   * Fires impact callbacks when scheduled time is reached
   */
  public updateImpacts(): void {
    const now = performance.now();
    const remainingImpacts: ScheduledImpact[] = [];

    for (const impact of this.scheduledImpacts) {
      if (impact.fired) {
        continue; // Already fired
      }

      if (now >= impact.scheduledTime) {
        // Fire the impact callback
        impact.fired = true;
        this.triggerAnimationImpact({
          unitId: impact.unitId,
          estimatedImpactTick: impact.estimatedImpactTick,
          attackType: impact.attackType
        });
      } else {
        // Keep for next frame
        remainingImpacts.push(impact);
      }
    }

    this.scheduledImpacts = remainingImpacts;
  }

  /**
   * Cancel all scheduled impacts for a unit
   * Called when unit dies before impact
   */
  public cancelImpactsForUnit(unitId: string): void {
    this.scheduledImpacts = this.scheduledImpacts.filter(
      impact => impact.unitId !== unitId
    );
  }

  /**
   * Get count of pending impacts (for debugging)
   */
  public getPendingImpactCount(): number {
    return this.scheduledImpacts.filter(i => !i.fired).length;
  }

  /**
   * Trigger animation impact callbacks
   */
  private triggerAnimationImpact(event: AnimationImpactEvent): void {
    this.animationImpactCallbacks.forEach(callback => callback(event));
  }
}
